import { useOTSTranslation } from 'i18n/useOTSTranslation';
import {
  Dispatch, SetStateAction, useCallback, useContext, useEffect,
} from 'react';
import { SubscriberDataEvent, useAnalytics } from 'util/analytics';
import { PlaningViewsEnum } from 'components/common/PlanYourTrip/constants';
import { isSouthernHemisphereCountry } from 'i18n/country';
import { useNoHeader } from 'util/useNoHeader';
import isLiftsUrlWhiteListed from 'util/isLiftsUrlWhiteListed';
import { isSkiRentalsLocale } from 'util/i18nFlags';
import classNames from 'classnames';
import { noOffersContext } from 'page-components/resort/_layout';
import { RESORT_IDS_TO_HIDE_LODGING_FOR } from 'ots-constants';
import { isResortWithoutWeSki } from 'util/isResortWithoutWeSki';
import { getLocale } from 'i18n/locale';
import ViewTab from './ViewTab';
import { ContextKey, TRKeysByContextType } from './types';

import styles from './styles.module.scss';

interface ITripControlsBlockProps {
  title: string
  liftsUrl?: string
  rentalUrl?: string
  lessonsUrl?: string
  activeView: PlaningViewsEnum
  onButtonClick: (value: PlaningViewsEnum) => void
  isPartner: boolean
  contextKey: ContextKey
  resortCountry?: string
  hasSkiRental: boolean
  redirectSkiRentalPath?: string
  bookFlightsCb?: () => void
  carRentalsCb?: () => void
  redirectLodgingPath?: string
  initialTab: PlaningViewsEnum,
  lodgingProvider?: 'tripadvisor' | 'weski',
  templateName?: string,
  setShouldHideLodging?: Dispatch<SetStateAction<boolean>>;
  resortId?: string;
  respectNoHeader?: boolean;
}

const TRKeysByContext: TRKeysByContextType<'common'> = {
  resort: 'tripWidget.resortLodging',
  region: 'tripWidget.regionLodging',
  weski: 'tripWidget.resortHolidays',
};

const TripControlBlock = (
  {
    title,
    liftsUrl,
    activeView,
    rentalUrl: rentalServerUrl,
    redirectSkiRentalPath,
    lessonsUrl,
    onButtonClick,
    isPartner,
    contextKey,
    hasSkiRental,
    resortCountry,
    redirectLodgingPath,
    initialTab = PlaningViewsEnum.lodging,
    lodgingProvider,
    templateName,
    setShouldHideLodging,
    resortId,
    respectNoHeader = true,
  }: ITripControlsBlockProps,
) => {
  const { t } = useOTSTranslation('common');
  const analytic = useAnalytics();
  const isEUresort = !(isSouthernHemisphereCountry(resortCountry));
  const { noHeader } = useNoHeader();
  const effectiveNoHeader = respectNoHeader ? noHeader : false;
  const isSkiRentalAPage = hasSkiRental && isSkiRentalsLocale();
  const rentalTabIsAnExternalUrl = !isSkiRentalAPage && isPartner;
  const rentalTabIsAForm = isSkiRentalAPage && initialTab === PlaningViewsEnum.rental;
  const rentalTabIsAnInternalUrl = isSkiRentalAPage && initialTab !== PlaningViewsEnum.rental && !!redirectSkiRentalPath;
  const rentalTabIsAnTab = (isSkiRentalAPage && initialTab !== PlaningViewsEnum.rental && !redirectSkiRentalPath) || (!isSkiRentalAPage && !isPartner);
  const useRentalTabAsUrl = rentalTabIsAnExternalUrl || rentalTabIsAnInternalUrl;
  const useRentalTabAsTab = rentalTabIsAForm || rentalTabIsAnTab;
  const urlToUseForRental = rentalTabIsAnInternalUrl ? redirectSkiRentalPath : rentalServerUrl;
  const showRentalTab = (((!isEUresort && isPartner) || isEUresort) && rentalServerUrl) || isSkiRentalAPage;
  const showLifts = liftsUrl && (isPartner || isLiftsUrlWhiteListed(liftsUrl));
  const withAnalytic = !(effectiveNoHeader || (!liftsUrl && !lessonsUrl && !showRentalTab && !isPartner));
  const useLodgingAsUrl = activeView === PlaningViewsEnum.flights || activeView === PlaningViewsEnum.carRental;

  const locale = getLocale();
  const noOffers = useContext(noOffersContext);
  const hasLodgingPage = !!redirectLodgingPath && (lodgingProvider === 'weski' || !noOffers) && !RESORT_IDS_TO_HIDE_LODGING_FOR.includes(resortId ?? '');
  const shouldHideWeSkiLodging = isResortWithoutWeSki(locale, resortId?.toString() || '');
  const lessonsTabActsAsLink = !shouldHideWeSkiLodging && hasLodgingPage;

  const onLodgingClick = useCallback(() => {
    if (!useLodgingAsUrl) {
      onButtonClick(PlaningViewsEnum.lodging);
    }
  }, [isEUresort, useLodgingAsUrl]);

  const onLiftsClick = useCallback(() => {
    analytic.subcriberEvent(SubscriberDataEvent.LiftAccessNavigation, { url: liftsUrl });
  }, [analytic, liftsUrl]);

  const onLessonsClick = useCallback(() => {
    if (!lessonsTabActsAsLink) {
      onButtonClick(PlaningViewsEnum.lessons);
    }

    if (lessonsTabActsAsLink) {
      analytic.subcriberEvent(SubscriberDataEvent.LessonsWidget, { url: lessonsUrl });
    }
  }, [analytic, lessonsTabActsAsLink, lessonsUrl, onButtonClick]);

  const onRentalClick = useCallback(() => {
    if (useRentalTabAsTab) {
      onButtonClick(PlaningViewsEnum.rental);
      return;
    }
    if (rentalTabIsAnExternalUrl && withAnalytic) {
      analytic.subcriberEvent(SubscriberDataEvent.RentalsWidget, { url: rentalServerUrl });
    }
  }, [analytic, rentalServerUrl, rentalTabIsAnExternalUrl, useRentalTabAsTab]);

  const applyActiveStyles = useCallback((key: PlaningViewsEnum) => {
    const activeStyle = key === activeView ? styles.activeTab : styles.viewTab;

    return classNames(activeStyle);
  }, [activeView, initialTab]);

  const isLodgingMode = initialTab === PlaningViewsEnum.lodging;

  const getActiveLodgingName = () => {
    let newContextKey = contextKey;
    if (lodgingProvider === 'weski') {
      newContextKey = lodgingProvider;
    }

    if (activeView === PlaningViewsEnum.lodging && isLodgingMode) {
      return `${title} ${t(TRKeysByContext[newContextKey], { [newContextKey]: '' })}`;
    }
    return t(TRKeysByContext[newContextKey]);
  };

  const activeLodgingName = getActiveLodgingName();

  const showMoreThanOneTab = !(templateName === 'Region Lodging' || templateName === 'Resort Lodging');

  const noOtherTabsShown = showMoreThanOneTab && (!showRentalTab && !showLifts && (!lessonsUrl || !isPartner));

  if (lodgingProvider === 'tripadvisor' && noOtherTabsShown && noOffers) {
    setShouldHideLodging?.(true);
  }
  // Determine which tabs are visible
  const visibleTabs: PlaningViewsEnum[] = [];
  if (!effectiveNoHeader && showMoreThanOneTab && hasLodgingPage && !shouldHideWeSkiLodging) {
    visibleTabs.push(PlaningViewsEnum.lodging);
  }
  if (!effectiveNoHeader && showRentalTab && showMoreThanOneTab && useRentalTabAsTab) {
    visibleTabs.push(PlaningViewsEnum.rental);
  }
  if (!effectiveNoHeader && showLifts && showMoreThanOneTab) {
    // There is no PlaningViewsEnum.lifts, so we skip adding it
    // visibleTabs.push(PlaningViewsEnum.lifts);
  }
  if (!effectiveNoHeader && lessonsUrl && showMoreThanOneTab) {
    visibleTabs.push(PlaningViewsEnum.lessons);
  }

  // If activeView is not visible, switch to the first visible tab
  useEffect(() => {
    if (!hasLodgingPage || shouldHideWeSkiLodging) {
      return;
    }

    if (visibleTabs.length > 0 && !visibleTabs.includes(activeView)) {
      onButtonClick(visibleTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, showRentalTab, showLifts, lessonsUrl, showMoreThanOneTab, effectiveNoHeader, resortId, hasLodgingPage, shouldHideWeSkiLodging]);
  return (
    <div className={styles.tripControlBlock}>
      {!effectiveNoHeader && showMoreThanOneTab && hasLodgingPage && !shouldHideWeSkiLodging && !noOtherTabsShown && (
        <ViewTab
          title={activeLodgingName}
          className={classNames(applyActiveStyles(PlaningViewsEnum.lodging), lodgingProvider === 'weski' && styles.weskiText,
            noOtherTabsShown && styles.alignStart)}
          onClick={onLodgingClick}
          url={useLodgingAsUrl ? redirectLodgingPath : undefined}
        />
      )}
      {!effectiveNoHeader && showRentalTab && showMoreThanOneTab && (
        <ViewTab
          title={t('tripWidget.skiRental')}
          className={applyActiveStyles(PlaningViewsEnum.rental)}
          onClick={onRentalClick}
          url={useRentalTabAsUrl ? urlToUseForRental : undefined}
        />
      )}
      {!effectiveNoHeader && showLifts && showMoreThanOneTab && (
        <ViewTab
          title={t('tripWidget.liftAccess')}
          className={styles.tabLink}
          onClick={onLiftsClick}
          url={liftsUrl}
        />
      )}
      {!effectiveNoHeader && lessonsUrl && isPartner && showMoreThanOneTab && (
        <ViewTab
          title={t('tripWidget.skiLessons')}
          className={applyActiveStyles(PlaningViewsEnum.lessons)}
          onClick={onLessonsClick}
          url={lessonsTabActsAsLink ? lessonsUrl : undefined}
          isExternal
        />
      )}
    </div>
  );
};

export default TripControlBlock;
